class GiftBuilderV2 {
  constructor() {
    this.page = document.getElementById('gift-builder-v2');
    if (!this.page) return;

    this.minItems = parseInt(this.page.dataset.minItems) || 3;
    this.giftBoxVariantId = this.page.dataset.giftBoxVariant;
    this.monogramAddonVariantId = this.page.dataset.monogramAddonVariant;
    this.compDiscountCode = this.page.dataset.compDiscountCode;

    // Every product — Step 1 Gisele PJs AND Step 2 accessories alike — now goes through the real
    // Quick View drawer and lands in the real Shopify cart the moment its native Add to Cart
    // succeeds. There's no more separate "pending" local-only list; everything here is a
    // confirmed real cart line, keyed by that line's `key`.
    this.confirmedItems = new Map();
    this.pendingQuickview = null; // { productId } while waiting for the resulting cart line
    this.lastKnownCartItems = [];

    // Monogram is fully decoupled from the drawer now — its own popup, its own state, keyed by
    // productId (string): { text, font, cartLineKey, feeAdded }. Can be set before or after that
    // product's Quick View add; reconcileMonogram() ties the two together either way.
    this.monogramSelections = new Map();
    this.activeMonogramProductId = null;
    this.monoFont = 'italic';

    this.giftBoxChecked = true;
    this.giftNote = '';

    // Accordion — each step auto-collapses once it hits its own required count (read from the
    // step's data-min-for-step) and is always reopenable via its header. autoCollapsed is a
    // one-shot flag per step so a manual reopen doesn't immediately get auto-closed again while
    // the shopper is still editing that step; it resets if the count drops back below the
    // requirement (e.g. an item removed), so a future re-completion can auto-collapse again.
    this.stepState = {};
    this.page.querySelectorAll('.gb-step[data-min-for-step]').forEach(section => {
      const stepNum = section.id.replace('gb-step-', '');
      this.stepState[stepNum] = { open: true, autoCollapsed: false, minForStep: parseInt(section.dataset.minForStep) || 1 };
    });

    this.bindQuickviewIntegration();
    this.bindMonogramModalEvents();
    this.bindStepAccordionEvents();
    this.bindTabEvents();
    this.bindColsScrollEvents();
    this.bindSidebarEvents();
    this.bindMobileEvents();
    this.bindGiftBoxEvents();
    this.bindGiftMessageEvents();
    this.seedCartSnapshot();
    this.updateUI();
  }

  async seedCartSnapshot() {
    try {
      const res = await fetch(`${window.theme?.routes?.cart || '/cart'}.js`);
      const cart = await res.json();
      this.lastKnownCartItems = cart.items;
    } catch (e) {
      this.lastKnownCartItems = [];
    }
  }

  /* ---- STEP ACCORDION ----
     Each step's header is a real <button> (bindStepAccordionEvents) so the shopper can always
     manually expand/collapse to change a selection. evaluateStepAccordion (called from
     updateUI, i.e. after every cart change) auto-collapses a step the moment it first reaches
     its own required count, then auto-scrolls to whichever step is next — so there's no manual
     scrolling to get from a completed Step 1 to Step 2. */
  bindStepAccordionEvents() {
    this.page.querySelectorAll('[data-gb-step-toggle]').forEach(header => {
      header.addEventListener('click', () => {
        const stepNum = header.dataset.gbStepToggle;
        this.toggleStep(stepNum, !this.stepState[stepNum].open);
      });
    });
  }

  toggleStep(stepNum, open) {
    const state = this.stepState[stepNum];
    if (!state) return;
    state.open = open;
    const section = document.getElementById(`gb-step-${stepNum}`);
    const header = this.page.querySelector(`[data-gb-step-toggle="${stepNum}"]`);
    if (section) section.classList.toggle('gb-step--collapsed', !open);
    if (header) header.setAttribute('aria-expanded', String(open));
  }

  evaluateStepAccordion() {
    const items = [...this.confirmedItems.values()];
    Object.keys(this.stepState).forEach(stepNum => {
      const state = this.stepState[stepNum];
      const count = items.filter(i => i.step === stepNum).length;

      const summary = document.getElementById(`gb-step-${stepNum}-summary`);
      if (summary) {
        summary.hidden = count === 0;
        summary.textContent = count > 0 ? `✓ ${count} item${count === 1 ? '' : 's'} selected` : '';
      }

      if (count >= state.minForStep) {
        if (state.open && !state.autoCollapsed) {
          state.autoCollapsed = true;
          this.toggleStep(stepNum, false);
          // Scroll to whichever step comes right after this one, if any.
          const section = document.getElementById(`gb-step-${stepNum}`);
          const nextStep = section?.nextElementSibling?.classList.contains('gb-step') ? section.nextElementSibling : null;
          if (nextStep) {
            setTimeout(() => nextStep.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
          }
        }
      } else {
        // Dropped back below the requirement (an item was removed) — allow a future
        // re-completion to auto-collapse again, but don't force the step back open; that stays
        // under the shopper's manual control per toggleStep().
        state.autoCollapsed = false;
      }
    });
  }

  /* ---- STYLE TILE ROW SCROLLING ---- */
  bindColsScrollEvents() {
    const cols = this.page.querySelector('.gb-cols');
    const prevBtn = document.getElementById('gb-cols-prev');
    const nextBtn = document.getElementById('gb-cols-next');
    if (!cols || !prevBtn || !nextBtn) return;

    const scrollByAmount = () => cols.clientWidth * 0.8;
    prevBtn.addEventListener('click', () => cols.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' }));
    nextBtn.addEventListener('click', () => cols.scrollBy({ left: scrollByAmount(), behavior: 'smooth' }));

    const updateArrows = () => {
      const maxScroll = cols.scrollWidth - cols.clientWidth;
      prevBtn.hidden = cols.scrollLeft <= 4;
      nextBtn.hidden = maxScroll <= 4 || cols.scrollLeft >= maxScroll - 4;
    };
    cols.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);
    updateArrows();
  }

  /* ---- STYLE FILTER TABS ----
     "All Styles" was removed — the first tab (marked gb-col--active in the liquid) is the
     default view. The grid itself has no server-side filtering, so the constructor calls
     setFilter() once on load to actually hide everything but that first tab's products;
     without this the shopper would see every style at once on page load, defeating the point. */
  bindTabEvents() {
    this.page.querySelectorAll('.gb-col').forEach(tab => {
      tab.addEventListener('click', () => this.setFilter(tab.dataset.filter, tab));
    });
    document.getElementById('gb-no-results')?.querySelector('.gb-no-results__reset')
      ?.addEventListener('click', () => {
        const firstTab = this.page.querySelector('.gb-col');
        if (firstTab) this.setFilter(firstTab.dataset.filter, firstTab);
      });

    const activeTab = this.page.querySelector('.gb-col--active');
    if (activeTab) this.setFilter(activeTab.dataset.filter, activeTab);
  }

  setFilter(filterKey, activeTab) {
    this.page.querySelectorAll('.gb-col').forEach(t => {
      t.classList.remove('gb-col--active');
      t.setAttribute('aria-selected', 'false');
    });
    activeTab.classList.add('gb-col--active');
    activeTab.setAttribute('aria-selected', 'true');

    const cards = document.querySelectorAll('#gb-gisele-grid .gb-card');
    let visible = 0;
    cards.forEach(card => {
      const styleKeys = (card.dataset.style || '').split(' ').filter(Boolean);
      const show = filterKey === 'all' || styleKeys.includes(filterKey);
      card.classList.toggle('gb-card--hidden', !show);
      if (show) visible++;
    });

    const noResults = document.getElementById('gb-no-results');
    const grid = document.getElementById('gb-gisele-grid');
    if (noResults && grid) {
      noResults.hidden = visible > 0;
      grid.hidden = visible === 0;
    }

    const label = document.getElementById('gb-filter-label');
    if (label) {
      const titleEl = activeTab.querySelector('.gb-col__title');
      const titleText = titleEl ? titleEl.textContent.trim() : '';
      label.textContent = filterKey === 'all' ? 'Showing all styles' : `Showing: ${titleText}`;
    }
  }

  /* ---- QUICK VIEW INTEGRATION (both Step 1 and Step 2 — every card uses the same drawer) ----
     The real Quick View form submits straight to /cart/add.js itself — we can't intercept that,
     so: on click, we note which product a Quick View was opened for (pendingQuickview); once
     that submit succeeds, theme:cart:change fires with the fresh cart, we diff it against our
     last snapshot to find the new line, and record it as "confirmed" (confirmQuickviewLine).

     Monogram is deliberately NOT injected into that form ahead of time anymore — an earlier
     version tried to sneak hidden fields into the Quick View form before the shopper submitted
     it, timed to whenever its AJAX content finished loading. That worked in isolated testing but
     was timing-dependent in practice and had no fallback if the timing was ever off, so it would
     occasionally add the product without the monogram on the very first attempt (the shopper
     then had to reopen the monogram popup and re-save to fix it). Simpler and fully reliable
     instead: let the Quick View add the product plain, then immediately patch the resulting line
     with the monogram via the exact same follow-up cart update used when a monogram is saved
     *after* the product's already in the cart (reconcileMonogram) — one path, always taken,
     works the same way every time regardless of which order the shopper does things in. */
  bindQuickviewIntegration() {
    this.page.addEventListener('gift-builder:quickview-opened', e => {
      this.pendingQuickview = { productId: String(e.detail.productId) };
    });

    document.addEventListener('theme:cart:change', e => this.handleCartChange(e.detail.cart));
  }

  async handleCartChange(cart) {
    if (!this.pendingQuickview) {
      this.lastKnownCartItems = cart.items;
      return;
    }
    const previousKeys = new Set(this.lastKnownCartItems.map(i => i.key));
    const newLine = cart.items.find(i => !previousKeys.has(i.key));
    this.lastKnownCartItems = cart.items;
    if (!newLine) return; // unrelated cart change (e.g. header cart poll); keep waiting

    const { productId } = this.pendingQuickview;
    this.pendingQuickview = null;
    await this.confirmQuickviewLine(newLine, productId);
  }

  async confirmQuickviewLine(line, productId) {
    const card = this.page.querySelector(`.gb-card[data-product-id="${productId}"]`);
    const step = card?.dataset.step;

    this.confirmedItems.set(line.key, {
      key: line.key,
      productId,
      step,
      variantId: line.variant_id,
      quantity: line.quantity,
      title: line.product_title || line.title,
      size: line.variant_title,
      price: line.price,
      priceFormatted: this.formatMoney(line.price),
      image: line.image,
      monogram: null,
    });

    if (card) card.classList.add('gb-card--selected');

    this.updateUI();

    // If a monogram was already staged for this product (saved via the popup before this Quick
    // View add), attach it now through the same reliable follow-up path used when it's saved
    // afterward instead — see reconcileMonogram(). This is what guarantees it lands the first
    // time: the Quick View's own submit is never relied on to carry the monogram itself.
    const mono = this.monogramSelections.get(productId);
    if (mono && mono.text && !mono.cartLineKey) {
      await this.reconcileMonogram(productId, mono);
    }
  }

  async removeConfirmedItem(key) {
    try {
      await fetch(`${window.theme?.routes?.cart || '/cart'}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    } finally {
      const item = this.confirmedItems.get(key);
      this.confirmedItems.delete(key);
      if (item) {
        const card = this.page.querySelector(`.gb-card[data-product-id="${item.productId}"]`);
        if (card) card.classList.remove('gb-card--selected');
      }
      this.updateUI();
    }
  }

  /* ---- MONOGRAM POPUP ----
     Fully separate from the Quick View drawer — its own button, its own modal. Saving here
     either (a) stages the monogram locally if the product isn't in the cart yet — confirmQuickviewLine
     picks it up and applies it automatically the moment that product IS added — or (b) if it's
     already a confirmed cart line, applies it immediately via reconcileMonogram(). Either way, the
     shopper can do these two steps in either order and it lands the same way both times. */
  bindMonogramModalEvents() {
    this.overlay = document.getElementById('gb-monogram-overlay');
    this.modal = document.getElementById('gb-monogram-modal');
    this.monoInput = document.getElementById('gb-monogram-input');
    this.monoCounter = document.getElementById('gb-monogram-counter');
    this.monoAck = document.getElementById('gb-monogram-ack');
    this.monoSaveBtn = document.getElementById('gb-monogram-save');
    this.monoProductTitle = document.getElementById('gb-monogram-product-title');
    if (!this.modal) return;

    this.page.querySelectorAll('[data-gb-monogram-btn]').forEach(btn => {
      btn.addEventListener('click', () => this.openMonogramModal(btn.dataset.productId));
    });

    document.getElementById('gb-monogram-close')?.addEventListener('click', () => this.closeMonogramModal());
    document.getElementById('gb-monogram-cancel')?.addEventListener('click', () => this.closeMonogramModal());
    this.overlay?.addEventListener('click', () => this.closeMonogramModal());

    this.monoInput.addEventListener('input', () => {
      const clean = this.monoInput.value.replace(/[^a-zA-Z ]/g, '').slice(0, 6).toUpperCase();
      if (clean !== this.monoInput.value) this.monoInput.value = clean;
      const left = 6 - this.monoInput.value.length;
      this.monoCounter.textContent = `${left} character${left === 1 ? '' : 's'} left`;
      this.updateMonogramSaveState();
    });
    this.monoAck.addEventListener('change', () => this.updateMonogramSaveState());
    this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(b => b.classList.remove('gb-monogram-modal__font-btn--active'));
        btn.classList.add('gb-monogram-modal__font-btn--active');
        this.monoFont = btn.dataset.font;
      });
    });
    this.monoSaveBtn.addEventListener('click', () => this.saveMonogram());
  }

  openMonogramModal(productId) {
    this.activeMonogramProductId = productId;
    const card = this.page.querySelector(`.gb-card[data-product-id="${productId}"]`);
    this.monoProductTitle.textContent = card?.dataset.productTitle || '';

    const existing = this.monogramSelections.get(productId);
    this.monoInput.value = existing ? existing.text : '';
    this.monoFont = existing ? existing.font : 'italic';
    this.monoAck.checked = !!existing;
    this.modal.querySelectorAll('[data-gb-monogram-font-btn]').forEach(b => {
      b.classList.toggle('gb-monogram-modal__font-btn--active', b.dataset.font === this.monoFont);
    });
    const left = 6 - this.monoInput.value.length;
    this.monoCounter.textContent = `${left} character${left === 1 ? '' : 's'} left`;
    this.updateMonogramSaveState();

    this.modal.hidden = false;
    this.overlay.hidden = false;
    this.monoInput.focus();
  }

  closeMonogramModal() {
    this.modal.hidden = true;
    this.overlay.hidden = true;
    this.activeMonogramProductId = null;
  }

  updateMonogramSaveState() {
    this.monoSaveBtn.disabled = !(this.monoInput.value.trim().length > 0 && this.monoAck.checked);
  }

  async saveMonogram() {
    const productId = this.activeMonogramProductId;
    if (!productId) return;
    const text = this.monoInput.value.trim();
    if (!text || !this.monoAck.checked) return;

    const existing = this.monogramSelections.get(productId) || {};
    const mono = { text, font: this.monoFont, cartLineKey: existing.cartLineKey || null, feeAdded: existing.feeAdded || false };
    this.monogramSelections.set(productId, mono);

    const btn = this.page.querySelector(`[data-gb-monogram-btn][data-product-id="${productId}"]`);
    if (btn) {
      btn.textContent = `Monogram: ${text}`;
      btn.classList.add('gb-card__monogram-btn--active');
    }

    this.closeMonogramModal();
    await this.reconcileMonogram(productId, mono);
  }

  // The one and only place a monogram actually gets attached to a cart line — via a follow-up
  // cart update + the $15 fee (only once, guarded by mono.feeAdded / mono.cartLineKey). Called
  // from two places: automatically from confirmQuickviewLine right after a product is added (the
  // common case — monogram saved first), and from saveMonogram if the product's already in the
  // cart (monogram saved after). If neither has happened yet, this is a no-op; it stays staged in
  // monogramSelections and confirmQuickviewLine will call it the moment that product is added.
  async reconcileMonogram(productId, mono) {
    const confirmed = [...this.confirmedItems.values()].find(i => i.productId === productId);
    if (!confirmed) return;

    try {
      const cartRes = await fetch(`${window.theme?.routes?.cart || '/cart'}.js`);
      const cart = await cartRes.json();
      const line = cart.items.find(i => i.key === confirmed.key);
      const existingProps = (line && line.properties) || {};

      await fetch(`${window.theme?.routes?.cart || '/cart'}/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: confirmed.key,
          quantity: confirmed.quantity || 1,
          properties: {
            ...existingProps,
            'Single Line Text': mono.text,
            'Font Style': mono.font === 'typewriter' ? 'Typewriter' : 'Italic',
            LPROP: 'Final Sale',
          },
        }),
      });

      if (!mono.feeAdded && this.monogramAddonVariantId) {
        await fetch(`${window.theme?.routes?.cart || '/cart'}/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(this.monogramAddonVariantId), quantity: 1 }] }),
        });
        mono.feeAdded = true;
      }

      mono.cartLineKey = confirmed.key;
      confirmed.monogram = { text: mono.text };
      this.updateUI();
    } catch (e) {
      console.error('Gift builder: failed to attach monogram to existing cart line', e);
    }
  }

  /* ---- UI ---- */
  updateUI() {
    const items = [...this.confirmedItems.values()];
    const count = items.length;
    // Gift box is complimentary within this experience, so it's intentionally left out of the
    // displayed total — see build notes for the underlying variant price and how that's reconciled.
    // Monogram is a real $15/item charge (the "Monogramming" add-on product), so it does count.
    const monogramFeeCents = items.filter(i => i.monogram).length * 1500;
    const total = items.reduce((s, i) => s + i.price, 0) + monogramFeeCents;
    const ready = count >= this.minItems;

    this.renderItemLists(items);
    this.updateProgress(count, ready);
    this.updateTotals(this.formatMoney(total));
    this.updateCTAs(ready, count);
    this.updateMobileBar(count, this.formatMoney(total));
    this.toggleEmptyStates(count);
    this.evaluateStepAccordion();
  }

  renderItemLists(items) {
    const html = items.map(item => this.itemHTML(item)).join('');
    ['gb-selected-items', 'gb-drawer-items'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = html;
    });
    this.page.querySelectorAll('.gb-selected-item__remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeConfirmedItem(btn.dataset.key));
    });
  }

  itemHTML(item) {
    return `<li class="gb-selected-item">
      <img src="${item.image}" alt="${item.title}" class="gb-selected-item__image" width="48" height="64" loading="lazy">
      <div class="gb-selected-item__details">
        <p class="gb-selected-item__name">${item.title}</p>
        ${item.size ? `<p class="gb-selected-item__size">Size: ${item.size}</p>` : ''}
        ${item.monogram ? `<p class="gb-selected-item__size">Monogram: ${item.monogram.text}</p>` : ''}
        <p class="gb-selected-item__price">${item.priceFormatted}${item.monogram ? ' + $15.00' : ''}</p>
      </div>
      <button class="gb-selected-item__remove" data-key="${item.key}" aria-label="Remove ${item.title}" type="button">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
      </button>
    </li>`;
  }

  updateProgress(count, ready) {
    const pct = Math.min((count / this.minItems) * 100, 100);
    ['gb-progress-fill', 'gb-drawer-progress-fill'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.width = pct + '%';
    });
    ['gb-progress-text', 'gb-drawer-progress-text'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (ready) {
        el.textContent = "You're all set — ready to gift!";
        el.classList.add('gb-progress-text--ready');
      } else {
        const needed = this.minItems - count;
        el.textContent = `Add ${needed} more item${needed !== 1 ? 's' : ''} to complete your gift`;
        el.classList.remove('gb-progress-text--ready');
      }
    });
  }

  updateTotals(formatted) {
    ['gb-total-price', 'gb-drawer-total'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.textContent = formatted;
    });
  }

  updateCTAs(ready, count) {
    ['gb-add-to-cart', 'gb-drawer-add-to-cart'].forEach(id => {
      const btn = document.getElementById(id);
      if (!btn) return;
      btn.disabled = !ready;
      btn.setAttribute('aria-disabled', String(!ready));
    });
    ['gb-cta-note', 'gb-drawer-note'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('gb-cta-note--error');
      el.textContent = ready
        ? `${count} items selected — ready to gift!`
        : `Select at least ${this.minItems} items to complete your gift`;
    });
  }

  updateMobileBar(count, formatted) {
    const c = document.getElementById('gb-mobile-count');
    const t = document.getElementById('gb-mobile-total');
    if (c) c.textContent = `${count} item${count !== 1 ? 's' : ''} selected`;
    if (t) t.textContent = formatted;
  }

  toggleEmptyStates(count) {
    [['gb-empty-state', 'gb-selected-items'], ['gb-drawer-empty', 'gb-drawer-items']].forEach(([emptyId, listId]) => {
      const empty = document.getElementById(emptyId);
      const list = document.getElementById(listId);
      if (!empty || !list) return;
      empty.hidden = count > 0;
      list.hidden = count === 0;
    });
  }

  /* ---- GIFT BOX ---- */
  bindGiftBoxEvents() {
    document.querySelectorAll('.gb-gift-box-check').forEach(cb => {
      cb.addEventListener('change', e => {
        this.giftBoxChecked = e.target.checked;
        document.querySelectorAll('.gb-gift-box-check').forEach(c => c.checked = this.giftBoxChecked);
      });
    });
  }

  /* ---- GIFT MESSAGE — matches the cart drawer's gift note textarea ---- */
  bindGiftMessageEvents() {
    document.querySelectorAll('[data-gb-giftnote]').forEach(textarea => {
      textarea.addEventListener('input', e => {
        this.giftNote = e.target.value;
        document.querySelectorAll('[data-gb-giftnote]').forEach(t => {
          if (t !== e.target) t.value = this.giftNote;
        });
      });
    });
  }

  /* ---- MOBILE DRAWER ---- */
  bindMobileEvents() {
    document.getElementById('gb-mobile-open')?.addEventListener('click', () => this.openDrawer());
    document.getElementById('gb-drawer-close')?.addEventListener('click', () => this.closeDrawer());
    document.getElementById('gb-drawer-overlay')?.addEventListener('click', () => this.closeDrawer());
  }

  openDrawer() {
    const drawer = document.getElementById('gb-drawer');
    const overlay = document.getElementById('gb-drawer-overlay');
    drawer?.classList.add('gb-drawer--open');
    drawer?.setAttribute('aria-hidden', 'false');
    if (overlay) { overlay.style.display = 'block'; requestAnimationFrame(() => overlay.classList.add('gb-drawer-overlay--visible')); }
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    const drawer = document.getElementById('gb-drawer');
    const overlay = document.getElementById('gb-drawer-overlay');
    drawer?.classList.remove('gb-drawer--open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (overlay) { overlay.classList.remove('gb-drawer-overlay--visible'); setTimeout(() => { overlay.style.display = ''; }, 300); }
    document.body.style.overflow = '';
  }

  /* ---- ADD TO CART ----
     Every product is already in the real cart via its own Quick View add — this button's only
     job now is to add the complimentary gift box (if checked), apply the gift note, and redirect
     through the discount code so the gift box + any monogram fees ring up free. */
  bindSidebarEvents() {
    ['gb-add-to-cart', 'gb-drawer-add-to-cart'].forEach(id => {
      document.getElementById(id)?.addEventListener('click', () => this.addToCart());
    });
  }

  async addToCart() {
    const btns = ['gb-add-to-cart', 'gb-drawer-add-to-cart'].map(id => document.getElementById(id)).filter(Boolean);
    if (btns[0]?.disabled) return;
    btns.forEach(b => { b.classList.add('gb-cta-btn--loading'); b.textContent = 'Adding...'; });

    try {
      if (this.giftBoxChecked && this.giftBoxVariantId) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(this.giftBoxVariantId), quantity: 1 }] }),
        });
        if (!res.ok) {
          // Shopify's /cart/add.js error responses carry a real, shopper-readable reason in
          // .description or .message — surface it instead of failing silently.
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'Something went wrong adding your gift box to the cart.');
        }
      }

      if (this.giftNote && this.giftNote.trim() !== '') {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: this.giftNote.trim() }),
        });
      }

      btns.forEach(b => {
        b.classList.remove('gb-cta-btn--loading');
        b.classList.add('gb-cta-btn--success');
        b.textContent = 'Added to Cart!';
      });

      // Redirecting through /discount/<code> silently applies that discount code to the cart —
      // no code box, no typing — before landing on /cart. This is what keeps the gift box and
      // monogram comps scoped to *this flow only*: the discount code itself does nothing until
      // triggered, and this redirect is the only thing that triggers it.
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      const destination = this.compDiscountCode
        ? `/discount/${encodeURIComponent(this.compDiscountCode)}?redirect=${encodeURIComponent(cartUrl)}`
        : cartUrl;
      setTimeout(() => { window.location.href = destination; }, 800);

    } catch (err) {
      btns.forEach(b => {
        b.classList.remove('gb-cta-btn--loading');
        b.textContent = 'Add Gift to Cart';
      });
      this.setCtaError(err.message || 'Something went wrong. Please try again.');
      console.error('Gift builder cart error:', err);
    }
  }

  // Shows a submission error in place of the progress note. Once the shopper changes their
  // selection, updateCTAs() (called from updateUI()) overwrites this with accurate progress text
  // again, so there's no separate "clear" path needed.
  setCtaError(message) {
    ['gb-cta-note', 'gb-drawer-note'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.textContent = message;
        el.classList.add('gb-cta-note--error');
      }
    });
  }

  // Shopify's standard money_format token replacement (the same algorithm the theme's own
  // bundled Currency helper uses — that helper isn't exposed on window for us to call directly,
  // since it's private to assets/theme.js's build, so this reimplements it against the same
  // window.theme.moneyFormat the rest of the theme already renders from, instead of a hand-rolled
  // "$X USD" string that doesn't match this shop's actual money format setting).
  formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const defaultOption = (opt, def) => (typeof opt === 'undefined' || opt === null ? def : opt);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    const match = format.match(placeholderRegex);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }
}

document.addEventListener('DOMContentLoaded', () => new GiftBuilderV2());
